// src/hooks/usePlacementTest.js
import { useState, useCallback, useEffect, useRef } from 'react';
import { LEVELS, SKILLS, getQuestion } from '../data/placementBank';
import { AUDIO_MAP } from '../data/audioIndex'; // ← AÑADIR
import { compareNormalized } from '../utils/textUtils';


const LEVEL_INDEX = { A1: 0, A2: 1, B1: 2, B2: 3, C1: 4, C2: 5 };
const INDEX_LEVEL = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const SKILL_QUOTA = {
  placement: { grammar: 4, vocabulary: 4, reading: 4, listening: 3 },
  master:    { grammar: 12, vocabulary: 12, reading: 10, listening: 6 },
};



// ── Tiempo por tipo de pregunta ──────────────────────────────────────────────
// ✏️  AÑADIDO: listening usa 45 s (audio ~15 s + margen para responder)
const TIME_BY_TYPE = {
  grammar:    20,
  vocabulary: 20,
  reading:    50,
  listening:  45,   // ← NEW
};


const MODE_CONFIG = {
  placement: { totalQuestions: 15, startLevel: 'B1' },
  master:    { totalQuestions: 40, startLevel: 'B1' },
};


// ── Skill balancer ───────────────────────────────────────────────────────────
// ✏️  Ahora incluye 'listening' en el conteo de skills
const getNextSkill = (history, mode = 'placement') => {
  const quota = SKILL_QUOTA[mode];

  // Contar cuántas de cada skill ya se usaron
  const skillCounts = {};
  SKILLS.forEach(s => { skillCounts[s] = 0; });
  history.forEach(h => {
    if (skillCounts[h.skill] !== undefined) skillCounts[h.skill]++;
  });

  // Filtrar las que aún no alcanzaron su cuota
  const available = SKILLS.filter(s => skillCounts[s] < (quota[s] ?? 99));

  // Si todas alcanzaron cuota (no debería pasar), fallback al mínimo
  const pool = available.length > 0 ? available : SKILLS;

  // De las disponibles, elegir la que tiene menos preguntas
  return pool.sort((a, b) => skillCounts[a] - skillCounts[b])[0];
};


// ── Cálculo de nivel final ───────────────────────────────────────────────────
const calculateFinalLevel = (history) => {
  if (history.length === 0) return 'A1';

  const levelStats = {};
  LEVELS.forEach(lvl => {
    const atLevel = history.filter(h => h.level === lvl);
    if (atLevel.length === 0) return;
    const correct = atLevel.filter(h => h.correct).length;
    levelStats[lvl] = {
      total: atLevel.length,
      correct,
      rate: correct / atLevel.length,
    };
  });

  // ── Cálculo ponderado original ────────────────────────────────────────────
  let weightedSum = 0;
  let totalWeight = 0;
  history.forEach((item, idx) => {
    const posWeight  = idx + 1;
    const lvlWeight  = LEVEL_INDEX[item.level] + 1;
    const weight     = posWeight * lvlWeight;
    const levelValue = LEVEL_INDEX[item.level];
    const score      = item.correct ? levelValue + 0.5 : levelValue - 0.5;
    weightedSum += score * weight;
    totalWeight += weight;
  });
  const avg = weightedSum / totalWeight;

  let adjustment = 0;
  Object.entries(levelStats).forEach(([lvl, stats]) => {
    if (stats.total >= 2) {
      if (stats.rate >= 0.80) adjustment += 0.3;
      if (stats.rate <= 0.40) adjustment -= 0.3;
    }
  });

  let anchorIdx = null;
  const anchors = Object.entries(levelStats)
    .filter(([_, s]) => s.total >= 3 && s.rate >= 0.60);
  if (anchors.length > 0) {
    const bestAnchor = anchors.sort((a, b) => b[1].total - a[1].total)[0];
    anchorIdx = LEVEL_INDEX[bestAnchor[0]];
  }

  let finalAvg = avg + adjustment;
  if (anchorIdx !== null) finalAvg = (finalAvg + anchorIdx) / 2;

  let idx = Math.max(0, Math.min(5, Math.round(finalAvg)));

  // ── ✅ NUEVO: techo por tasa de aciertos global ───────────────────────────
  // Evita dar niveles altos con % bajo de aciertos
  const globalRate = history.filter(h => h.correct).length / history.length;

  const RATE_CEILING = [
    { minRate: 0.85, maxLevel: 5 }, // C2 — necesita 85%+
    { minRate: 0.70, maxLevel: 4 }, // C1 — necesita 70%+
    { minRate: 0.55, maxLevel: 3 }, // B2 — necesita 55%+
    { minRate: 0.40, maxLevel: 2 }, // B1 — necesita 40%+
    { minRate: 0.25, maxLevel: 1 }, // A2 — necesita 25%+
    { minRate: 0.00, maxLevel: 0 }, // A1 — cualquier cosa
  ];

  const ceiling = RATE_CEILING.find(r => globalRate >= r.minRate);
  if (ceiling) idx = Math.min(idx, ceiling.maxLevel);

  // ── ✅ NUEVO: piso por nivel más alto dominado (≥60% con ≥2 preguntas) ───
  // Evita bajar injustamente si el usuario demostró dominio real
  let floorIdx = 0;
  Object.entries(levelStats).forEach(([lvl, stats]) => {
    if (stats.total >= 2 && stats.rate >= 0.60) {
      floorIdx = Math.max(floorIdx, LEVEL_INDEX[lvl]);
    }
  });
  idx = Math.max(idx, floorIdx);

  return INDEX_LEVEL[idx];
};


// ── Skill scores ─────────────────────────────────────────────────────────────
// ✏️  Funciona automáticamente con el nuevo SKILLS que incluye 'listening'
const calculateSkillScores = (history) => {
  const scores = {};
  SKILLS.forEach(skill => {
    const skillHistory = history.filter(h => h.skill === skill);
    if (skillHistory.length === 0) { scores[skill] = 0; return; }
    const correct = skillHistory.filter(h => h.correct).length;
    scores[skill] = Math.round((correct / skillHistory.length) * 100);
  });
  return scores;
};


// ─────────────────────────────────────────────────────────────────────────────
export const usePlacementTest = (mode = 'placement') => {
  const config = MODE_CONFIG[mode];

  // ── Estados del test ────────────────────────────────────────────────────────
  const [currentLevel,   setCurrentLevel]   = useState(config.startLevel);
  const [history,        setHistory]        = useState([]);
  const [usedIds,        setUsedIds]        = useState([]);
  const [currentQ,       setCurrentQ]       = useState(() =>
    getQuestion(config.startLevel, SKILLS[0], [])
  );
  // ✏️  NUEVO phase 'audio': se activa cuando la pregunta es de tipo listening
  //     Flujo: 'testing' → (si listening) → 'audio' → (audio termina) → 'testing' → 'feedback' → ...
  const [phase,          setPhase]          = useState('testing');
  const [lastCorrect,    setLastCorrect]    = useState(null);
  const [questionNumber, setQuestionNumber] = useState(1);

  // ── Estados del timer ────────────────────────────────────────────────────────
  const [timeLeft,  setTimeLeft]  = useState(20);
  const [totalTime, setTotalTime] = useState(0);
  const timerRef      = useRef(null);
  const totalTimerRef = useRef(null);

  const totalQuestions = config.totalQuestions;

  // ── Timer global ─────────────────────────────────────────────────────────────
  useEffect(() => {
    totalTimerRef.current = setInterval(() => {
      setTotalTime(prev => prev + 1);
    }, 1000);
    return () => clearInterval(totalTimerRef.current);
  }, []);

  // ── Timer por pregunta ────────────────────────────────────────────────────────
  const startTimer = useCallback((q) => {
    clearInterval(timerRef.current);
    // ✏️  Usa TIME_BY_TYPE dinámico, cubre grammar/vocabulary/reading/listening
    const seconds = TIME_BY_TYPE[q?.skill] ?? 20;
    setTimeLeft(seconds);

    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // ── Dispara timer al cambiar de pregunta ──────────────────────────────────────
  // ✏️  Si la pregunta es listening → cambia a phase 'audio' sin iniciar timer.
  //     El timer arrancará cuando el componente llame a onAudioReady().
  useEffect(() => {
    if (!currentQ || phase !== 'testing') return;

    if (currentQ.type === 'listening') {
      setPhase('audio');   // espera a que el componente reproduzca el audio
      return;
    }

    startTimer(currentQ);
    return () => clearInterval(timerRef.current);
  }, [questionNumber]); // ← solo dispara al cambiar pregunta

  // ── Timeout automático ────────────────────────────────────────────────────────
  useEffect(() => {
    if (timeLeft === 0 && phase === 'testing') {
      answerQuestion(null);
    }
  }, [timeLeft]);

  // ── Limpieza ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      clearInterval(totalTimerRef.current);
    };
  }, []);

  // ── onAudioReady ─────────────────────────────────────────────────────────────
  // ✏️  NUEVA función. El componente de audio la llama cuando el audio termina.
  //     Transiciona de 'audio' → 'testing' e inicia el timer.
  const onAudioReady = useCallback(() => {
    if (phase !== 'audio' || !currentQ) return;
    setPhase('testing');
    startTimer(currentQ);
  }, [phase, currentQ, startTimer]);

  // ── Lógica de respuesta ───────────────────────────────────────────────────────
  const answerQuestion = useCallback((selectedOption) => {
    // ✏️  Acepta respuesta durante 'testing' O cuando el timer llega a 0 desde 'audio'
    if ((phase !== 'testing' && phase !== 'audio') || !currentQ) return;

    clearInterval(timerRef.current);

    const isCorrect = selectedOption !== null && compareNormalized(selectedOption, currentQ.a);
    setLastCorrect(isCorrect);
    setPhase('feedback');

    const newHistory = [...history, {
      id:      currentQ.id,
      level:   currentQ.level,
      skill:   currentQ.skill,
      correct: isCorrect,
    }];
    const newUsedIds = [...usedIds, currentQ.id];

    setHistory(newHistory);
    setUsedIds(newUsedIds);

    setTimeout(() => {
      const nextNumber = questionNumber + 1;

      if (nextNumber > totalQuestions) {
        clearInterval(totalTimerRef.current);
        setPhase('finished');
        return;
      }

      const currentIdx = LEVEL_INDEX[currentLevel];
      const nextIdx    = isCorrect
        ? Math.min(5, currentIdx + 1)
        : Math.max(0, currentIdx - 1);
      const nextLevel  = INDEX_LEVEL[nextIdx];
      setCurrentLevel(nextLevel);

      const nextSkill = getNextSkill(newHistory, mode);
      let nextQ = getQuestion(nextLevel, nextSkill, newUsedIds);

      if (!nextQ) {
        for (const offset of [1, -1, 2, -2]) {
          const fallbackIdx = Math.max(0, Math.min(5, nextIdx + offset));
          nextQ = getQuestion(INDEX_LEVEL[fallbackIdx], nextSkill, newUsedIds);
          if (nextQ) break;
        }
      }

      if (!nextQ) {
        clearInterval(totalTimerRef.current);
        setPhase('finished');
        return;
      }

      setCurrentQ(nextQ);
      setQuestionNumber(nextNumber);  // dispara useEffect del timer ✅
      setLastCorrect(null);
      setPhase('testing');            // useEffect decidirá si pasa a 'audio' ✅
    }, 900);

  }, [phase, currentQ, history, usedIds, currentLevel, questionNumber, totalQuestions]);

  // ── Resultados finales ────────────────────────────────────────────────────────
  const finalLevel   = calculateFinalLevel(history);
  const skillScores  = calculateSkillScores(history);
  const totalCorrect = history.filter(h => h.correct).length;
  const accuracy     = history.length > 0
    ? Math.round((totalCorrect / history.length) * 100)
    : 0;

    // ── Audio URL para preguntas listening ────────────────────────────────────────
// Cuenta cuántas preguntas listening de este nivel ya se han hecho → subdivision
const listeningAtCurrentLevel = history.filter(
  h => h.skill === 'listening' && h.level === currentQ?.level
).length;
const audioSubdivision = (listeningAtCurrentLevel % 3) + 1; // 1, 2 o 3
const audioId = currentQ?.type === 'listening'
  ? `${currentQ.level}.${audioSubdivision}`
  : null;
const currentAudioUrl = audioId ? AUDIO_MAP[audioId]?.url ?? null : null;


  return {
    currentQ,
    currentLevel,
    phase,           // 'testing' | 'audio' | 'feedback' | 'finished'
    lastCorrect,
    questionNumber,
    totalQuestions,
    finalLevel,
    skillScores,
    totalCorrect,
    accuracy,
    history,
    timeLeft,
    totalTime,
    currentAudioUrl, 
  audioId,    
    answer:       answerQuestion,
    onAudioReady,                 // ← NEW: llama esto cuando el audio termina
  };
};
